import { useFormikContext } from "formik";
import { useMemo, type ReactNode } from "react";
import { OrganizationMember } from "@common/dto/organization/organization-member.dto";
import type { ApplyCloseTemplateWizardValues } from "./applyCloseTemplate.schema";
import type { WorkspaceMemberForClientAssignment } from "./applyCloseTemplateOrganizationMembers";
import {
    TemplateApplicationContext,
    type TemplateApplicationContextValue,
} from "./templateApplicationContext";

interface ProviderProps {
    children: ReactNode;
    members: OrganizationMember[];
    getMemberLabel: (userId: number | null) => string;
    workspaceMembersMap: Record<string, WorkspaceMemberForClientAssignment[]>;
}

export const TemplateApplicationProvider = ({
    children,
    members,
    getMemberLabel,
    workspaceMembersMap,
}: ProviderProps) => {
    const { values, setFieldValue } =
        useFormikContext<ApplyCloseTemplateWizardValues>();

    const value: TemplateApplicationContextValue = useMemo(
        () => ({
            members,
            getMemberLabel,
            workspaceMembersMap,
            getEntityRow: (entityIdStr: string) =>
                values.entityRows[entityIdStr],
            setEntityRowField: (entityIdStr, field, fieldValue) => {
                void setFieldValue(
                    `entityRows.${entityIdStr}.${String(field)}`,
                    fieldValue,
                );
            },
        }),
        [
            getMemberLabel,
            members,
            workspaceMembersMap,
            setFieldValue,
            values.entityRows,
        ],
    );

    return (
        <TemplateApplicationContext.Provider value={value}>
            {children}
        </TemplateApplicationContext.Provider>
    );
};
